import { useCallback, useEffect, useMemo, useState } from "react";
import { useAlert } from "react-alert";
import { useTranslation } from "react-i18next";
import { collection, getDocs, query, where } from "firebase/firestore";
import { aggregateCashlessConsumption } from "../../../utility/cashlessConsumption";
import Table from "../../../components/table/Table";
import { db } from "../../../firebase";
import "./CashlessModal.scss";

const CardInfoRow = ({ label, value }) => (
  <div className="card-info-row">
    <span className="card-info-label">{label}:</span>
    <span className="card-info-value">{value}</span>
  </div>
);

export function CardConsumptionModal({ show, onHide, card, orgId }) {
  const { t } = useTranslation(["cashless", "common"]);
  const alert = useAlert();
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [aggregatedProducts, setAggregatedProducts] = useState([]);

  const totalSpent = useMemo(
    () => aggregatedProducts.reduce((sum, product) => sum + product.totalDue, 0),
    [aggregatedProducts],
  );

  const totalQuantity = useMemo(
    () => aggregatedProducts.reduce((sum, product) => sum + product.quantity, 0),
    [aggregatedProducts],
  );

  const fetchCardOrders = useCallback(
    async (signal) => {
      setLoading(true);
      setLoadError(false);
      setAggregatedProducts([]);
      try {
        const snapshots = await Promise.all([
          getDocs(query(collection(db, "Orders"), where("cardId", "==", card.id), where("orgId", "==", orgId))),
          getDocs(query(collection(db, "Orders"), where("cashlessCardIds", "array-contains", card.id), where("orgId", "==", orgId))),
        ]);
        if (signal.aborted) return;
        const matchingOrders = new Map();
        snapshots.forEach((snapshot) => snapshot.docs.forEach((doc) => matchingOrders.set(doc.id, doc.data())));
        setAggregatedProducts(aggregateCashlessConsumption([...matchingOrders.values()], card.id));
      } catch (error) {
        console.error("Error fetching cashless card orders:", error);
        if (!signal.aborted) {
          setAggregatedProducts([]);
          setLoadError(true);
          alert.show(t("cardConsumptionError"), { type: "error" });
        }
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    },
    [alert, card?.id, orgId, t],
  );

  useEffect(() => {
    if (!show || !card?.id || !orgId) return;
    const abortController = new AbortController();
    fetchCardOrders(abortController.signal);
    return () => abortController.abort();
  }, [show, card?.id, orgId, fetchCardOrders, refreshKey]);

  if (!show || !card) return null;

  return (
    <div className="modal fade show d-block cashless-modal">
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{t("cardConsumption")}</h5>
            <button type="button" className="btn-close" onClick={onHide} disabled={loading} />
          </div>

          <div className="modal-body">
            <div className="card-info card-consumption-summary">
              <CardInfoRow label={t("cardId")} value={card?.id} />
              <CardInfoRow label={t("productsCount")} value={loading || loadError ? "—" : totalQuantity} />
              <CardInfoRow label={t("totalConsumed")} value={loading || loadError ? "—" : `${totalSpent.toFixed(2)}€`} />
            </div>

            {loading ? (
              <div className="card-consumption-table-container">
                <Table isLoading />
              </div>
            ) : loadError ? (
              <div className="card-consumption-empty-state" role="alert">
                <p className="confirmation-text">{t("cardConsumptionError")}</p>
                <button type="button" className="btn btn-cancel" onClick={() => setRefreshKey((key) => key + 1)}>{t("retry")}</button>
              </div>
            ) : aggregatedProducts.length === 0 ? (
              <div className="card-consumption-empty-state">
                <p className="confirmation-text mb-0">{t("noCashlessOrdersForCard")}</p>
              </div>
            ) : (
              <div className="card-consumption-table-container">
                <Table
                  columns={[
                    { key: "title", label: t("product") },
                    { key: "quantity", label: t("quantity") },
                    {
                      key: "totalDue",
                      label: t("amount"),
                      render: (value) => `${Number(value || 0).toFixed(2)}€`,
                    },
                  ]}
                  data={aggregatedProducts}
                />
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-cancel" onClick={onHide}>
              {t("close")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CardConsumptionModal;
